import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

const IOS_HINT_KEY = 'nj.iosInstallHintShown';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return iOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/**
 * Service Worker の更新通知と、インストール導線をまとめて出す。
 * iOSでは未インストールだとストレージが破棄されうるため、案内を1度だけ表示する（仕様書 §16.2）。
 */
export function PwaBanner() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({ onRegisterError: (e) => console.error('SW登録に失敗しました', e) });

  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [autoUpdating, setAutoUpdating] = useState(false);

  /**
   * 編集中でなければ自動で更新を適用する。
   * 手動確認だけにすると、古いビルドが配信され続けていることに気づけない。
   * 編集中（/new・/edit）は入力内容を失わないよう、明示的な操作を待つ（仕様書 §16.3）。
   */
  useEffect(() => {
    if (!needRefresh) return;
    const path = window.location.pathname;
    const editing = path === '/new' || path.endsWith('/edit');
    if (editing) return;
    setAutoUpdating(true);
    const id = window.setTimeout(() => updateServiceWorker(true), 1200);
    return () => window.clearTimeout(id);
  }, [needRefresh, updateServiceWorker]);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as InstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    if (!isIosSafari()) return;
    if (localStorage.getItem(IOS_HINT_KEY)) return;
    setShowIosHint(true);
  }, []);

  const dismissIos = () => {
    localStorage.setItem(IOS_HINT_KEY, '1');
    setShowIosHint(false);
  };

  if (needRefresh) {
    return (
      <div className="toast">
        {autoUpdating ? (
          <span>新しいバージョンに更新しています…</span>
        ) : (
          <>
            <span>新しいバージョンがあります（編集が終わったら更新してください）</span>
            <button className="toast-btn" onClick={() => updateServiceWorker(true)}>
              更新
            </button>
            <button className="toast-close" onClick={() => setNeedRefresh(false)} aria-label="閉じる">
              ✕
            </button>
          </>
        )}
      </div>
    );
  }

  if (installEvent) {
    return (
      <div className="toast">
        <span>ホーム画面に追加できます</span>
        <button
          className="toast-btn"
          onClick={async () => {
            await installEvent.prompt();
            await installEvent.userChoice;
            setInstallEvent(null);
          }}
        >
          インストール
        </button>
        <button className="toast-close" onClick={() => setInstallEvent(null)} aria-label="閉じる">
          ✕
        </button>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div className="toast toast-tall">
        <span>
          ホーム画面に追加すると、オフラインで安定して使えます。
          <br />
          共有ボタン <b>⬆︎</b> →「ホーム画面に追加」
        </span>
        <button className="toast-close" onClick={dismissIos} aria-label="閉じる">
          ✕
        </button>
      </div>
    );
  }

  return null;
}
